/**
 * Shared "Restore deleted custom columns" modal.
 * Props: basePath (e.g. "/subcontractors"), testIdPrefix.
 */
import { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { ArrowCounterClockwise, Archive } from "@phosphor-icons/react";

function humanTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })} · ${d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
  } catch { return String(iso); }
}

export default function RestoreColumnsModal({ open, onClose, onRestored, basePath, testIdPrefix = "restore-columns" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`${basePath}/columns`, { params: { scope: "deleted" } });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
      setItems([]);
    } finally { setLoading(false); }
  }, [basePath]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const restore = async (cid, label) => {
    setRestoring(cid);
    try {
      await api.post(`${basePath}/columns/${cid}/restore`);
      toast.success(`Restored "${label}"`);
      onRestored && onRestored();
      load();
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    } finally { setRestoring(""); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid={`${testIdPrefix}-modal`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowCounterClockwise size={16} className="text-emerald-600" /> Restore custom columns
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] text-emerald-800">
            Any values entered in the column's cells are preserved and reappear the moment you restore it.
          </div>
          {loading && <div className="py-4 text-center text-sm text-slate-500">Loading…</div>}
          {!loading && items.length === 0 && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-6 text-center text-xs text-slate-500 italic" data-testid={`${testIdPrefix}-empty`}>
              <Archive size={22} className="mx-auto text-slate-300 mb-2" />
              No deleted custom columns.
            </div>
          )}
          {!loading && items.length > 0 && (
            <div className="space-y-2 max-h-[380px] overflow-y-auto">
              {items.map((c) => (
                <div key={c.id} className="rounded-lg border border-slate-200 bg-white p-3 flex items-center gap-3" data-testid={`${testIdPrefix}-item-${c.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-slate-800 truncate">{c.label}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">Type: <span className="font-semibold uppercase tracking-widest">{c.type}</span></div>
                    {c.deleted_by_name ? (
                      <div className="text-[10px] text-slate-400 mt-0.5">Deleted by {c.deleted_by_name} · {humanTime(c.deleted_at)}</div>
                    ) : null}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => restore(c.id, c.label)}
                    disabled={restoring === c.id}
                    className="bg-emerald-500 hover:bg-emerald-600"
                    data-testid={`${testIdPrefix}-btn-${c.id}`}
                  >
                    <ArrowCounterClockwise size={12} className="mr-1" />
                    {restoring === c.id ? "Restoring…" : "Restore"}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
